/**
 * runtime/api.ts - the injectable runtime for the community-admin surface.
 *
 * This package is TENANT-AGNOSTIC. It carries NO dialer origin, NO tenant id, and
 * NO Firebase coupling. The consuming app (e.g. greenmap = the dvw4ea tenant) calls
 * `configureCommunityAdmin()` once at startup to inject:
 *   - `apiBase`      the dialer ORIGIN (no path), e.g. the metabus-prod dialer-api URL
 *   - `tenantId`     the tenant path segment for the authless PUBLIC routes
 *   - `getAuthToken` a provider that returns the operator's bearer token (or null)
 *
 * The dialer mounts the community-engagement surface under two prefixes:
 *   PUBLIC (authless, tenant-scoped by path) - /api/public/community/{tenant_id}/...
 *   ADMIN  (authed, tenant resolved from the bearer token) - /api/community/...
 *
 * Every per-tenant value is injected, so a SECOND tenant gets the whole surface by
 * configuring a different apiBase/tenantId/token provider - no code change here.
 */

export interface CommunityAdminQrDomains {
  /** Origin the QR "greenmap" link target points at (with ?ref= attribution). */
  mapDomain?: string;
  /** Origin the QR "website" link target points at (with ?ref= attribution). */
  websiteDomain?: string;
}

export interface CommunityAdminRuntimeConfig {
  /** The dialer origin (no trailing slash, no path). */
  apiBase: string;
  /** Tenant path segment for the authless PUBLIC routes. */
  tenantId: string;
  /** Returns the operator's bearer token, or null when not signed in. */
  getAuthToken: () => Promise<string | null>;
  /**
   * Per-tenant QR link-target origins (optional; the QR-campaigns capability is
   * abstracted and off for tenants that do not use it). Injected here so the
   * package carries no tenant domain literal.
   */
  qrDomains?: CommunityAdminQrDomains;
  /**
   * Per-tenant support contact address, shown in this package's own
   * failure/next-step copy (e.g. the site-content editor's save-failure banner
   * and load-failure state). The package itself NEVER names a person - a
   * consuming app injects its own contact address here, or leaves it unset,
   * in which case this package falls back to "the person who manages your
   * website" wording.
   */
  supportEmail?: string;
}

let runtimeConfig: CommunityAdminRuntimeConfig | null = null;

/**
 * Configure the community-admin runtime. Call once at app startup, BEFORE any
 * service/hook issues a request. Idempotent - the last call wins.
 */
export function configureCommunityAdmin(config: CommunityAdminRuntimeConfig): void {
  runtimeConfig = {
    apiBase: config.apiBase.replace(/\/$/, ''),
    tenantId: config.tenantId,
    getAuthToken: config.getAuthToken,
    qrDomains: config.qrDomains,
    supportEmail: config.supportEmail,
  };
}

/**
 * The injected per-tenant support contact address, or undefined when the
 * consuming app did not set one. Callers should fall back to a generic
 * "the person who manages your website" phrase when this is undefined -
 * this package never names a person.
 */
export function getSupportEmail(): string | undefined {
  return requireConfig().supportEmail;
}

/** The injected per-tenant QR link-target domains (empty object when unset). */
export function getQrDomains(): CommunityAdminQrDomains {
  return requireConfig().qrDomains ?? {};
}

/** True once configureCommunityAdmin() has run. */
export function isCommunityAdminConfigured(): boolean {
  return runtimeConfig !== null;
}

function requireConfig(): CommunityAdminRuntimeConfig {
  if (!runtimeConfig) {
    throw new Error(
      'community-admin is not configured - call configureCommunityAdmin() at app startup before using any service or hook.',
    );
  }
  return runtimeConfig;
}

/** The injected dialer origin (no trailing slash). */
export function getCommunityApiBase(): string {
  return requireConfig().apiBase;
}

/** The injected tenant id (public-route path segment). */
export function getTenantId(): string {
  return requireConfig().tenantId;
}

/**
 * Build an authless PUBLIC community route URL (prefix /api/public/community),
 * tenant-scoped by the injected tenant id. `path` is the segment after the
 * tenant id, e.g. 'events', 'collections', 'submissions', 'email-signup'.
 */
export function publicCommunityUrl(path: string): string {
  const cfg = requireConfig();
  return `${cfg.apiBase}/api/public/community/${cfg.tenantId}/${path.replace(/^\//, '')}`;
}

/**
 * Build an authed ADMIN community route URL (prefix /api/community). The tenant is
 * resolved server-side from the operator's bearer token, so there is NO tenant id
 * path param. `path` is the segment after the prefix, e.g. 'collections',
 * 'submissions/{id}/moderate', 'collections/{id}/winners'.
 */
export function adminCommunityUrl(path: string): string {
  return `${requireConfig().apiBase}/api/community/${path.replace(/^\//, '')}`;
}

/** The dialer's per-request tenant override (backend/auth/tenant_override.py). */
export const ACT_AS_TENANT_HEADER = 'X-Act-As-Tenant';

/**
 * Headers for an authenticated admin call: the injected bearer token, the
 * configured tenant, plus any extra headers. Throws if the token provider
 * returns null (not signed in).
 *
 * WHY THE TENANT IS SENT. The admin routes resolve the tenant from the caller,
 * and without this header that means the operator's stored `default_tenant`,
 * which is a single global field on their user doc. An operator who belongs to
 * several tenants therefore reached whichever one they last switched to, not
 * the one whose app they had open: signing in to a community app you legitimately
 * administer could answer 404, because the feature gate was being asked about a
 * different tenant entirely. A consuming app already knows which tenant it is
 * (it is injected here and used on every public URL), so it says so.
 *
 * THIS GRANTS NOTHING. `apply_tenant_override` re-points the request only when
 * `authorize(user, Action.TENANT_ACCESS, tenantId)` passes, and answers 403
 * before the route runs otherwise, so the header can only select among
 * memberships the caller already holds. It also recomputes role, permissions and
 * modules from the TARGET tenant's membership, so being an admin of one tenant
 * carries no authority into another.
 *
 * ONLY WHEN CONFIGURED. Apps that use this package for tenant-agnostic pieces
 * only (the email-confirmation screen in `apps/mas` and `apps/operator`) inject
 * `tenantId: ''`, and send NO header at all, which is byte-identical to the
 * behavior before this existed.
 */
export async function authHeaders(
  extra: Record<string, string> = {},
): Promise<Record<string, string>> {
  const cfg = requireConfig();
  const token = await cfg.getAuthToken();
  if (!token) {
    throw new Error('Not authenticated');
  }
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  const tenantId = (cfg.tenantId || '').trim();
  if (tenantId) {
    headers[ACT_AS_TENANT_HEADER] = tenantId;
  }
  return { ...headers, ...extra };
}

/** A failed authed community call, carrying the HTTP status for gate decisions. */
export class CommunityApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'CommunityApiError';
    this.status = status;
  }
}

/**
 * GET an authed ADMIN community route and return the parsed JSON body. Sends the
 * injected bearer token; the dialer resolves the tenant server-side and
 * 401/403/404s a caller without access or community engagement enabled. Throws a
 * CommunityApiError (carrying the status) on any non-2xx response.
 */
export async function adminCommunityGet<T = unknown>(path: string): Promise<T> {
  const response = await fetch(adminCommunityUrl(path), {
    method: 'GET',
    headers: await authHeaders(),
  });
  if (!response.ok) {
    throw new CommunityApiError(
      `API error: ${response.status} ${response.statusText}`,
      response.status,
    );
  }
  return (await response.json()) as T;
}

/**
 * The ADMIN GATE / server enablement probe. The operator's bearer token PLUS their
 * tenant membership IS the gate. We probe one authed, side-effect-free community
 * route (`GET /api/community/events`, guarded by the dialer's read auth + the
 * community-engagement enable check). The dialer returns:
 *   200 -> valid token AND tenant access AND community engagement enabled. Grant.
 *   401 -> no / invalid token (not signed in). Deny.
 *   403 -> valid token but no access to this tenant. Deny.
 *   404 -> community engagement not enabled for the caller's tenant. Deny.
 * Any other status or a network error denies (deny-by-default). A tenant WITHOUT
 * community_engagement resolves to false here, so the mount gate renders nothing.
 */
export async function checkCommunityAccess(): Promise<boolean> {
  const token = await requireConfig().getAuthToken();
  if (!token) {
    return false;
  }
  try {
    await adminCommunityGet('events');
    return true;
  } catch (error) {
    if (!(error instanceof CommunityApiError)) {
      // Network / unexpected error: deny by default.
      console.error('Community access probe failed (denying access):', error);
    }
    return false;
  }
}

/**
 * GET an arbitrary authed dialer route (outside the /api/community prefix) and
 * return the parsed JSON. Used by the audience/leads reads that route through the
 * dialer's existing /api/prospects endpoint. `path` starts with '/api/...'.
 */
export async function dialerAuthedGet<T = unknown>(path: string): Promise<T> {
  const response = await fetch(`${getCommunityApiBase()}${path}`, {
    method: 'GET',
    headers: await authHeaders(),
  });
  if (!response.ok) {
    throw new CommunityApiError(
      `API error: ${response.status} ${response.statusText}`,
      response.status,
    );
  }
  return (await response.json()) as T;
}

/**
 * Ask the dialer to send a verification email to the SIGNED-IN user's own
 * address. There is no address parameter by design: the backend reads the
 * address from the caller's Firebase token, so this can never be pointed at
 * someone else.
 *
 * Reachable while DENIED. The dialer gates this route on the token alone, not on
 * an authorized user doc, because the denial a user needs to escape happens
 * during token-to-user resolution. A user who is refused everywhere else can
 * still call this one route.
 *
 * The dialer answers 202 for every outcome (sent, already verified, tenant not
 * configured, transport down) so nothing here can be used to probe which
 * addresses exist. Callers should report "check your inbox", never "sent".
 * A 429 means the resend limit was hit and is surfaced as its own error so the
 * UI can say something useful.
 */
export async function requestEmailVerification(): Promise<void> {
  const response = await fetch(
    `${getCommunityApiBase()}/api/auth/email-verification/resend`,
    { method: 'POST', headers: await authHeaders() },
  );
  if (!response.ok) {
    throw new CommunityApiError(
      `API error: ${response.status} ${response.statusText}`,
      response.status,
    );
  }
}
